'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useScrollLock } from '@/lib/hooks'

/**
 * CONFY Modal Design System
 *
 * A unified modal wrapper component that standardizes 11+ modal implementations.
 * Provides consistent styling, accessibility, and behavior across the platform.
 *
 * Features:
 * - Dark backdrop (bg-black/50) with click-to-close
 * - Entrance/exit animations (300ms scale + fade)
 * - Focus trapping within modal
 * - Escape key closes modal
 * - Body scroll lock when open
 * - Full ARIA attributes (role="dialog", aria-modal, aria-labelledby, aria-describedby)
 * - Responsive sizing with max-width constraints
 * - Optional close button in header
 *
 * Sizes:
 * - sm: max-w-sm (384px) - Confirmation dialogs, simple forms
 * - md: max-w-md (448px) - Standard modals, forms
 * - lg: max-w-lg (512px) - Complex forms, multi-step wizards
 * - xl: max-w-xl (576px) - Large content modals
 * - 2xl: max-w-2xl (672px) - Very large content
 * - full: max-w-4xl (896px) - Full-width modals for browsers/pickers
 *
 * @example
 * // Simple modal
 * <Modal isOpen={isOpen} onClose={handleClose} title="Confirm Action">
 *   <p>Are you sure you want to proceed?</p>
 *   <Modal.Footer>
 *     <Button variant="secondary" onClick={handleClose}>Cancel</Button>
 *     <Button onClick={handleConfirm}>Confirm</Button>
 *   </Modal.Footer>
 * </Modal>
 *
 * @example
 * // Modal with description
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Delete Item"
 *   description="This action cannot be undone."
 *   size="sm"
 * >
 *   <Modal.Footer>
 *     <Button variant="secondary" onClick={handleClose}>Cancel</Button>
 *     <Button variant="destructive" onClick={handleDelete}>Delete</Button>
 *   </Modal.Footer>
 * </Modal>
 */

const modalVariants = cva(
  'relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 w-full transform transition-all duration-300',
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        full: 'max-w-4xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface ModalProps extends VariantProps<typeof modalVariants> {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback when the modal should close */
  onClose: () => void
  /** Modal title displayed in the header */
  title?: string
  /** Optional description displayed below the title */
  description?: string
  /** Modal content */
  children: React.ReactNode
  /** Whether to show the close button in the header */
  showCloseButton?: boolean
  /** Whether clicking the backdrop closes the modal */
  closeOnBackdropClick?: boolean
  /** Whether pressing Escape closes the modal */
  closeOnEscape?: boolean
  /** Custom z-index for the modal (default: 50) */
  zIndex?: number
  /** Additional class names for the modal container */
  className?: string
  /** ID for the modal title (for aria-labelledby) */
  titleId?: string
  /** ID for the modal description (for aria-describedby) */
  descriptionId?: string
}

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      title,
      description,
      children,
      size,
      showCloseButton = true,
      closeOnBackdropClick = true,
      closeOnEscape = true,
      zIndex = 50,
      className,
      titleId,
      descriptionId,
    },
    ref
  ) => {
    const [isMounted, setIsMounted] = React.useState(false)
    const [isVisible, setIsVisible] = React.useState(false)
    const modalRef = React.useRef<HTMLDivElement>(null)
    const previousActiveElement = React.useRef<HTMLElement | null>(null)

    // Generate IDs for ARIA attributes
    const generatedTitleId = React.useId()
    const generatedDescriptionId = React.useId()
    const resolvedTitleId = titleId || generatedTitleId
    const resolvedDescriptionId = descriptionId || generatedDescriptionId

    // Handle mounting and visibility for animations
    React.useEffect(() => {
      if (isOpen) {
        setIsMounted(true)
        // Small delay to trigger enter animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true)
          })
        })
      } else {
        setIsVisible(false)
        // Wait for exit animation before unmounting
        const timer = setTimeout(() => {
          setIsMounted(false)
        }, 300)
        return () => clearTimeout(timer)
      }
    }, [isOpen])

    // Centralized scroll lock
    useScrollLock(isOpen)

    // Latest callbacks in refs: parents pass a fresh onClose on every render,
    // and re-running the focus effect for that would yank focus back to the
    // first focusable element (the close button) on each keystroke.
    const onCloseRef = React.useRef(onClose)
    const closeOnEscapeRef = React.useRef(closeOnEscape)
    React.useEffect(() => {
      onCloseRef.current = onClose
      closeOnEscapeRef.current = closeOnEscape
    })

    // Handle escape key, focus management. Runs once per open (and once more
    // when the portal mounts, so the initial focus finds the dialog).
    React.useEffect(() => {
      if (!isOpen) return

      // Store currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement

      // Handle escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && closeOnEscapeRef.current) {
          onCloseRef.current()
        }
      }

      document.addEventListener('keydown', handleEscape)

      // Focus first focusable element in modal
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElements && focusableElements.length > 0) {
        ;(focusableElements[0] as HTMLElement).focus()
      }

      return () => {
        document.removeEventListener('keydown', handleEscape)
        // Restore focus to previously focused element
        previousActiveElement.current?.focus()
      }
    }, [isOpen, isMounted])

    // Focus trap
    React.useEffect(() => {
      if (!isOpen) return

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return

        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )

        if (!focusableElements || focusableElements.length === 0) return

        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }

      document.addEventListener('keydown', handleTabKey)
      return () => document.removeEventListener('keydown', handleTabKey)
    }, [isOpen])

    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && closeOnBackdropClick) {
        onClose()
      }
    }

    if (!isMounted) return null

    return createPortal(
      <div
        className={cn(
          'fixed inset-0 flex items-center justify-center p-4 transition-opacity duration-300',
          isVisible ? 'bg-black/50' : 'bg-black/0'
        )}
        style={{ zIndex }}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? resolvedTitleId : undefined}
        aria-describedby={description ? resolvedDescriptionId : undefined}
      >
        <div
          ref={(node) => {
            // Handle both refs
            modalRef.current = node
            if (typeof ref === 'function') {
              ref(node)
            } else if (ref) {
              ref.current = node
            }
          }}
          className={cn(
            modalVariants({ size }),
            isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
              <div className="flex-1 pr-4">
                {title && (
                  <h2
                    id={resolvedTitleId}
                    className="text-xl font-semibold text-gray-900"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id={resolvedDescriptionId}
                    className="mt-1 text-sm text-gray-600"
                  >
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                  aria-label="Close modal"
                >
                  <X className="size-5" />
                </Button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-5">{children}</div>
        </div>
      </div>,
      document.body
    )
  }
)

Modal.displayName = 'Modal'

/**
 * Modal.Footer - Footer section for modal actions
 *
 * @example
 * <Modal.Footer>
 *   <Button variant="secondary" onClick={onClose}>Cancel</Button>
 *   <Button onClick={onConfirm}>Confirm</Button>
 * </Modal.Footer>
 */
interface ModalFooterProps {
  children: React.ReactNode
  className?: string
}

const ModalFooter = ({ children, className }: ModalFooterProps) => {
  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row gap-3 justify-end pt-4 border-t border-gray-100 -mx-6 px-6 -mb-5 pb-5 mt-5',
        className
      )}
    >
      {children}
    </div>
  )
}

ModalFooter.displayName = 'Modal.Footer'

/**
 * Modal.Body - Body section for modal content (when you need padding control)
 *
 * @example
 * <Modal.Body className="space-y-4">
 *   <Input label="Name" />
 *   <Input label="Email" />
 * </Modal.Body>
 */
interface ModalBodyProps {
  children: React.ReactNode
  className?: string
}

const ModalBody = ({ children, className }: ModalBodyProps) => {
  return <div className={cn('space-y-4', className)}>{children}</div>
}

ModalBody.displayName = 'Modal.Body'

// Attach subcomponents
const ModalWithSubcomponents = Modal as typeof Modal & {
  Footer: typeof ModalFooter
  Body: typeof ModalBody
}

ModalWithSubcomponents.Footer = ModalFooter
ModalWithSubcomponents.Body = ModalBody

export { ModalWithSubcomponents as Modal, modalVariants }
